/**
 * MemoryDetailPage — V12.x
 *
 * Page détail d'un item Mémoire publié (`/memoire/{slug}`).
 * - Images (cover + galerie) affichées à leur ratio naturel, sans rognage.
 * - Clic sur une image → lightbox plein écran.
 * - Vidéos YouTube intégrées (iframe responsive 16/9).
 * - Documents (PDF/archives) : liens de téléchargement.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { ArrowLeft, ExternalLink, Sparkles, Youtube, FileText, Download, Images } from "lucide-react";
import YouTubeEmbed from "@/components/media/YouTubeEmbed";
import LightboxDialog from "@/components/media/LightboxDialog";

const MEMORY_TYPE_LABELS = {
  article: "Article",
  archive: "Archive sportive",
  portrait_historique: "Portrait historique",
  competition_ancienne: "Compétition ancienne",
  photo_video: "Photo / vidéo",
  temoignage: "Témoignage",
  palmares_historique: "Palmarès historique",
  evenement_marquant: "Événement marquant",
};

export default function MemoryDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Lightbox state
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openLightbox = (src) => { setLightboxSrc(src); setLightboxOpen(true); };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/memory/${slug}`)
      .then((r) => setItem(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const gallery = useMemo(() => (Array.isArray(item?.media_urls) ? item.media_urls : []), [item]);
  const videos = useMemo(() => (Array.isArray(item?.youtube_urls) ? item.youtube_urls : []), [item]);
  const documents = useMemo(() => (Array.isArray(item?.documents) ? item.documents : []), [item]);

  if (loading) {
    return <div className="mx-auto max-w-4xl p-8"><Card className="p-6 text-center text-sm text-muted-foreground">Chargement…</Card></div>;
  }
  if (notFound || !item) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <EmptyState
          icon={Sparkles}
          title="Élément introuvable"
          description="Cet article Mémoire n'est plus disponible ou n'a pas encore été publié."
        />
        <div className="text-center mt-4">
          <Link to="/memoire" className="text-sm text-[hsl(var(--aito-lagoon))] hover:underline">← Retour à la Mémoire</Link>
        </div>
      </div>
    );
  }

  return (
    <article className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-8" data-testid="memory-detail">
      <Link to="/memoire" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3 w-3" /> Retour à la Mémoire
      </Link>

      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <Badge variant="outline" className="text-[10px]">{MEMORY_TYPE_LABELS[item.type] || item.type}</Badge>
        {item.year && (
          <Badge variant="outline" className="text-[10px] font-mono">
            {item.year}{item.period_end_year ? `–${item.period_end_year}` : ""}
          </Badge>
        )}
        {item.discipline && <span className="text-xs text-muted-foreground capitalize">· {item.discipline}</span>}
      </div>

      <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">{item.title}</h1>
      {item.short_description && (
        <p className="text-base text-muted-foreground mt-2 leading-relaxed">{item.short_description}</p>
      )}

      {/* ---- Cover — ratio naturel, plafonnée en hauteur, clic → lightbox ---- */}
      {item.image_url && (
        <button
          type="button"
          onClick={() => openLightbox(item.image_url)}
          className="my-6 block w-full rounded-lg overflow-hidden bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-gold))]"
          aria-label="Agrandir l'image"
          data-testid="memory-cover-trigger"
        >
          <img
            src={item.image_url}
            alt={item.title}
            className="mx-auto block h-auto w-auto max-w-full max-h-[75vh] object-contain cursor-zoom-in transition hover:opacity-95"
          />
        </button>
      )}

      {item.content && (
        <div className="prose prose-sm sm:prose-base max-w-none mt-4 whitespace-pre-wrap">{item.content}</div>
      )}

      {/* ---- Galerie — images à leur ratio naturel, lightbox ---- */}
      {gallery.length > 0 && (
        <section className="mt-8" data-testid="memory-gallery-section">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Images className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> Galerie
            <Badge variant="outline" className="text-[10px] font-mono">{gallery.length}</Badge>
          </h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {gallery.map((u, i) => (
              <button
                key={u + i}
                type="button"
                onClick={() => openLightbox(u)}
                className="group block rounded-md overflow-hidden bg-muted border focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-gold))]"
                aria-label={`Agrandir la photo ${i + 1}`}
                data-testid={`memory-gallery-item-${i}`}
              >
                <img
                  src={u}
                  alt={`${item.title} — média ${i + 1}`}
                  loading="lazy"
                  className="w-full h-auto object-contain cursor-zoom-in transition group-hover:opacity-95"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ---- Vidéos YouTube ---- */}
      {videos.length > 0 && (
        <section className="mt-8" data-testid="memory-videos-section">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Youtube className="h-4 w-4 text-red-600" /> Vidéos
            <Badge variant="outline" className="text-[10px] font-mono">{videos.length}</Badge>
          </h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((u, i) => (
              <YouTubeEmbed key={u + i} url={u} title={`${item.title} — vidéo ${i + 1}`} />
            ))}
          </div>
        </section>
      )}

      {/* ---- Documents ---- */}
      {documents.length > 0 && (
        <section className="mt-8" data-testid="memory-documents-section">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <FileText className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> Documents
            <Badge variant="outline" className="text-[10px] font-mono">{documents.length}</Badge>
          </h2>
          <ul className="mt-3 divide-y rounded-md border bg-card">
            {documents.map((d, i) => (
              <li key={(d.url || "") + i} className="flex items-center gap-3 p-3">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm leading-tight truncate">{d.label || "Document"}</div>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] font-mono text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] truncate block"
                  >
                    {d.url}
                  </a>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase">{d.kind || "doc"}</Badge>
                <Button asChild size="sm" variant="outline" data-testid={`memory-doc-download-${i}`}>
                  <a href={d.url} target="_blank" rel="noreferrer" download>
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Ouvrir
                  </a>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {item.tags?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-1.5">
          {item.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">#{t}</Badge>)}
        </div>
      )}

      {item.source_url && (
        <div className="mt-6 pt-4 border-t">
          <Button asChild variant="outline" size="sm">
            <a href={item.source_url} target="_blank" rel="noreferrer" data-testid="memory-source-link">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Voir la source originale
            </a>
          </Button>
        </div>
      )}

      <LightboxDialog
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        src={lightboxSrc}
        alt={item.title}
      />
    </article>
  );
}
